import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from "axios";
import { AuthService } from "@/services/AuthService";
import { store } from "@/store";
// import { getAccessToken } from "@/utils/tokenManager";
import { handleLogout } from "@/utils/logoutHandler";
import { ApiPopupManager } from "@/utils/popupUtils";
import { i18n } from "@/i18n";
import { showError } from "@/components/popup/Popups";

// 백엔드 응답 구조에 맞는 인터페이스
export interface BackendApiResponse<T = any> {
  status: number;
  message: string;
  data: T;
  paging?: {
    page: number;
    size: number;
    totalPages: number;
    totalElements: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export class ApiRepository {
  private baseURL: string;
  private defaultConfig: AxiosRequestConfig;
  private isRefreshing: boolean = false;
  private isLoggingOut: boolean = false; // 로그아웃 중 플래그
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];
  constructor(baseURL?: string) {
    this.baseURL = baseURL || import.meta.env.VITE_API_HOST || "http://localhost:9010";
    this.defaultConfig = {
      timeout: 10000,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    };
  }

  private getAuthHeaders(): Record<string, string> {
    // tokenManager를 통해 토큰 가져오기
    // let token = getAccessToken();

    // localStorage에 없으면 Vuex store에서 가져오기
    // if (!token && store && store.getters && store.getters.JWT) {
    //   token = store.getters.JWT;
    // }

    const headers: Record<string, string> = {};

    // if (token) {
    //   headers.Authorization = `Bearer ${token}`;
    // }

    // Site_id 헤더 자동 추가
    const selectedSiteId = localStorage.getItem("selectedSiteId");
    if (selectedSiteId) {
      headers["Site-Id"] = selectedSiteId;
    }

    if (store && store.getters && store.getters.locale) {
      const localeLanguageMap: Record<string, string> = {
        ko: "ko-KR,kr;q=0.9",
        en: "en-US,en;q=0.9",
      };
      const locale = store.getters.locale;
      headers["Accept-Language"] = localeLanguageMap[locale] || "en-US,en;q=0.9";
    }

    return headers;
  }

  // store가 안전하게 사용 가능한지 확인
  private isStoreAvailable(): boolean {
    return store && store.getters;
  }

  // 401 에러 체크 (HTTP 상태 코드 + 백엔드 응답 status 필드)
  private is401Error(error: any): boolean {
    // HTTP 상태 코드가 401인 경우
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return true;
    }

    // 백엔드 응답의 status 필드가 401인 경우
    if (error?.response?.status === 401) {
      return true;
    }

    return false;
  }

  // 성공 응답이지만 백엔드에서 401 에러를 보내는 경우 체크
  private isBackend401Error(response: any): boolean {
    if (response?.status === 401) {
      return true;
    }

    return false;
  }

  // 세션 타임아웃 체크 (code: "SESSION_TIMEOUT" 또는 logout: true)
  // 백엔드 응답 구조: { status: 401, message: "...", data: { code: "SESSION_TIMEOUT", logout: true } }
  private isSessionTimeout(error?: AxiosError, responseData?: any): boolean {
    // 데이터 추출: error.response.data 또는 responseData
    let data: any = error?.response?.data ?? responseData;
    console.log(data)
    // Axios가 body를 문자열로 넘기는 경우 파싱 (Content-Type 등에 따라 객체가 아닐 수 있음)
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return false;
      }
    }

    if (!data || typeof data !== "object" || data === null) {
      return false;
    }

    // 백엔드 응답 구조: data.data 안에 code와 logout이 있음
    if (data.data && typeof data.data === "object" && data.data !== null) {
      if (data.data.code === "SESSION_TIMEOUT" || data.data.logout === true) {
        return true;
      }
    }

    // 안전을 위해 최상위 레벨도 체크 (다른 응답 구조 대비)
    if (data.code === "SESSION_TIMEOUT" || data.logout === true) {
      return true;
    }

    return false;
  }

  // 401 에러 처리 및 토큰 재발급 (통합)
  private async handle401Error(error?: AxiosError, responseData?: any): Promise<any> {
    // 로그아웃 중이면 재발급 시도하지 않음 (무한 반복 방지)
    if (this.isLoggingOut) {
      throw new Error("Logout in progress");
    }

    // 세션 타임아웃 체크를 가장 먼저 수행 (토큰 재발급 API 호출 전에)
    if (this.isSessionTimeout(error, responseData)) {
      // 세션 타임아웃 팝업 표시 (공통 함수 사용)
      await showError(i18n.global.t("messages.apiSessionTimeout"));
      // 대기 중인 요청들 모두 실패 처리
      this.processQueue(new Error("Session timeout"), null);
      // 재발급 진행 중이어도 세션 타임아웃이면 중단하고 로그아웃
      this.isRefreshing = false;
      this.isLoggingOut = true; // 로그아웃 시작 플래그 설정
      handleLogout();
      throw new Error("Session timeout");
    }

    if (this.isRefreshing) {
      // 이미 토큰 재발급 중인 경우 대기 (세션 타임아웃이 아닌 경우에만)
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const authService = new AuthService();

      const refreshResponse = await authService.reissueToken();

      if (refreshResponse.status === 200 && refreshResponse.data) {
        // 새 토큰 저장
        authService.saveTokens(refreshResponse.data);

        // 새 토큰이 제대로 저장되었는지 확인
        // const newToken = getAccessToken();

        // 실패한 요청들을 새 토큰으로 재시도
        this.processQueue(null, refreshResponse.data.accessToken);

        // 원래 요청 재시도 (새 토큰으로 헤더 업데이트)
        if (error?.config) {
          // HTTP 401 에러인 경우
          const authHeaders = this.getAuthHeaders();
          error.config.headers = {
            ...error.config.headers,
            ...authHeaders,
            Authorization: `Bearer ${refreshResponse.data.accessToken}`,
          } as typeof error.config.headers;
          // 쿠키 포함 설정
          error.config.withCredentials = true;
          return axios(error.config);
        } else if (responseData) {
          // 백엔드 401 에러인 경우 - 호출한 곳에서 재시도 처리
          return refreshResponse.data.accessToken;
        }
      } else {
        throw new Error("Token reissue failed");
      }
    } catch (refreshError) {
      // 토큰 재발급 실패 시 로그아웃
      this.processQueue(refreshError, null);
      this.isLoggingOut = true; // 로그아웃 시작 플래그 설정
      handleLogout();
      throw refreshError;
    } finally {
      this.isRefreshing = false;
    }
  }

  // 실패한 요청 큐 처리
  private processQueue(error: any, token: string | null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  // 에러 처리
  private handleError(error: any): never {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      // 401 에러인 경우 토큰 재발급 시도
      if (this.is401Error(error)) {
        // 토큰 재발급 로직은 handle401Error에서 처리
        throw axiosError;
      }

      // 다른 HTTP 에러들
      if (axiosError.response) {
        const status = axiosError.response.status;
        const message = (axiosError.response.data as any).message || `HTTP ${status} 에러가 발생했습니다.`;

        throw new Error(message);
      }

      // 네트워크 에러
      if (axiosError.request) {
        throw new Error(i18n.global.t("messages.networkError"));
      }
    }

    // 기타 에러
    throw error;
  }

  async get<T>(endpoint: string, params?: any, config?: AxiosRequestConfig): Promise<BackendApiResponse<T>> {
    try {
      const authHeaders = this.getAuthHeaders();

      const response: AxiosResponse<BackendApiResponse<T>> = await axios.get(`${this.baseURL}${endpoint}`, {
        ...this.defaultConfig,
        ...config,
        withCredentials: true,
        headers: {
          ...this.defaultConfig.headers,
          ...authHeaders,
          ...config?.headers,
        },
        params,
      });

      // 성공 응답이지만 백엔드에서 401 에러를 보내는 경우 체크
      if (this.isBackend401Error(response.data)) {
        // 로그아웃 API는 401 에러를 무시하고 그냥 반환 (무한 반복 방지)
        const isLogoutEndpoint = endpoint.includes("/auth/logout") || endpoint.includes("/logout");
        if (isLogoutEndpoint) {
          return response.data;
        }

        // 세션 타임아웃 체크를 먼저 수행 (토큰 재발급 API 호출 전에)
        if (this.isSessionTimeout(undefined, response.data)) {
          // 세션 타임아웃 팝업 표시 (공통 함수 사용)
          await showError(i18n.global.t("messages.apiSessionTimeout"));
          this.processQueue(new Error("Session timeout"), null);
          this.isRefreshing = false;
          this.isLoggingOut = true; // 로그아웃 시작 플래그 설정
          handleLogout();
          throw new Error("Session timeout");
        }
        try {
          // 통합된 handle401Error 사용 (세션 타임아웃 체크 포함)
          const newToken = await this.handle401Error(undefined, response.data);

          if (newToken) {
            const authHeaders = this.getAuthHeaders();
            const retryResponse = await axios.get(`${this.baseURL}${endpoint}`, {
              ...this.defaultConfig,
              ...config,
              withCredentials: true, // 쿠키 포함
              headers: {
                ...this.defaultConfig.headers,
                ...authHeaders,
                Authorization: `Bearer ${newToken}`,
                ...config?.headers,
              },
              params,
            });

            return retryResponse.data;
          }
        } catch (refreshError) {
          throw refreshError;
        }
      } 

      return response.data;
    } catch (error: any) {
      // 로그아웃 API는 401 에러를 무시하고 그냥 반환 (무한 반복 방지)
      const isLogoutEndpoint = endpoint.includes("/auth/logout") || endpoint.includes("/logout");
      if (isLogoutEndpoint && this.is401Error(error)) {
        // 로그아웃 중 플래그 설정하여 재발급 시도 방지
        this.isLoggingOut = true;
        // 에러를 무시하고 빈 응답 반환
        return {
          status: 200,
          message: "Logout completed",
          data: {} as T,
        };
      }

      // 401 에러인 경우 토큰 재발급 시도
      if (this.is401Error(error)) {
        try {
          return await this.handle401Error(error);
        } catch (refreshError) {
          // 토큰 재발급 실패 시 원래 에러 처리
          throw this.handleError(error);
        }
      }
      throw this.handleError(error);
    }
  }

  async post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<BackendApiResponse<T>> {
    // 로그아웃 API는 토큰 재발급 로직에서 제외 (무한 반복 방지)
    const isLogoutEndpoint = endpoint.includes("/auth/logout") || endpoint.includes("/logout");
    
    try {
      const authHeaders = this.getAuthHeaders();

      const response: AxiosResponse<BackendApiResponse<T>> = await axios.post(`${this.baseURL}${endpoint}`, data, {
        ...this.defaultConfig,
        ...config,
        withCredentials: true,
        headers: {
          ...this.defaultConfig.headers,
          ...authHeaders,
          ...config?.headers,
        },
      });

      // 성공 응답이지만 백엔드에서 401 에러를 보내는 경우 체크
      if (this.isBackend401Error(response.data)) {
        // 로그아웃 API는 401 에러를 무시하고 그냥 반환 (무한 반복 방지)
        if (isLogoutEndpoint) {
          return response.data;
        }

        // 세션 타임아웃 체크를 먼저 수행 (토큰 재발급 API 호출 전에)
        if (this.isSessionTimeout(undefined, response.data)) {
          // 세션 타임아웃 팝업 표시 (공통 함수 사용)
          await showError(i18n.global.t("messages.apiSessionTimeout"));
          this.processQueue(new Error("Session timeout"), null);
          this.isRefreshing = false;
          this.isLoggingOut = true; // 로그아웃 시작 플래그 설정
          handleLogout();
          throw new Error("Session timeout");
        }
        try {
          // 통합된 handle401Error 사용 (세션 타임아웃 체크 포함)
          const newToken = await this.handle401Error(undefined, response.data);

          if (newToken) {
            const authHeaders = this.getAuthHeaders();
            const retryResponse = await axios.post(`${this.baseURL}${endpoint}`, data, {
              ...this.defaultConfig,
              ...config,
              withCredentials: true, // 쿠키 포함
              headers: {
                ...this.defaultConfig.headers,
                ...authHeaders,
                Authorization: `Bearer ${newToken}`,
                ...config?.headers,
              },
            });

            return retryResponse.data;
          }
        } catch (refreshError) {
          throw refreshError;
        }
      }

      return response.data;
    } catch (error: any) {
      // 로그아웃 API는 401 에러를 무시하고 그냥 반환 (무한 반복 방지)
      if (isLogoutEndpoint && this.is401Error(error)) {
        // 로그아웃 중 플래그 설정하여 재발급 시도 방지
        this.isLoggingOut = true;
        // 에러를 무시하고 빈 응답 반환
        return {
          status: 200,
          message: "Logout completed",
          data: {} as T,
        };
      }

      if (this.is401Error(error)) {
        try {
          return await this.handle401Error(error);
        } catch (refreshError) {
          throw this.handleError(error);
        }
      }
      throw this.handleError(error);
    }
  }

  async put<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<BackendApiResponse<T>> {
    try {
      const authHeaders = this.getAuthHeaders();
      const response: AxiosResponse<BackendApiResponse<T>> = await axios.put(`${this.baseURL}${endpoint}`, data, {
        ...this.defaultConfig,
        ...config,
        withCredentials: true,
        headers: {
          ...this.defaultConfig.headers,
          ...authHeaders,
          ...config?.headers,
        },
      });
      return response.data;
    } catch (error: any) {
      if (this.is401Error(error)) {
        try {
          return await this.handle401Error(error);
        } catch (refreshError) {
          throw this.handleError(error);
        }
      }
      throw this.handleError(error);
    }
  }

  async patch<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<BackendApiResponse<T>> {
    try {
      const authHeaders = this.getAuthHeaders();
      const response: AxiosResponse<BackendApiResponse<T>> = await axios.patch(`${this.baseURL}${endpoint}`, data, {
        ...this.defaultConfig,
        ...config,
        withCredentials: true,
        headers: {
          ...this.defaultConfig.headers,
          ...authHeaders,
          ...config?.headers,
        },
      });
      return response.data;
    } catch (error: any) {
      if (this.is401Error(error)) {
        try {
          return await this.handle401Error(error);
        } catch (refreshError) {
          throw this.handleError(error);
        }
      }
      throw this.handleError(error);
    }
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<BackendApiResponse<T>> {
    try {
      const authHeaders = this.getAuthHeaders();
      const response: AxiosResponse<BackendApiResponse<T>> = await axios.delete(`${this.baseURL}${endpoint}`, {
        ...this.defaultConfig,
        ...config,
        withCredentials: true,
        headers: {
          ...this.defaultConfig.headers,
          ...authHeaders,
          ...config?.headers,
        },
      });
      return response.data;
    } catch (error: any) {
      if (this.is401Error(error)) {
        try {
          return await this.handle401Error(error);
        } catch (refreshError) {
          throw this.handleError(error);
        }
      }
      throw this.handleError(error);
    }
  }
}
